import * as Sentry from '@sentry/browser'
import React, { useState, useEffect, ChangeEvent } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useRecoilState } from 'recoil'
import ReactTooltip from 'react-tooltip'
import {
  Switch,
  FormControl,
  TextInput,
  Dropdown,
  InputGroup,
  Loader,
  AutoSuggest,
} from 'components'
import { findAll } from 'features/Company/service'
import type { BranchForForm, Company, ILoaderOption, OptionProps } from 'types'
import { branchFormState } from 'recoils/atoms/branchFormAtom'
import {
  formatPhoneNumberOnBlur,
  getPhoneNumberMaxLength,
  validateKeyPress,
  setSelectedOption,
  trimFirstSpace,
  handleDisableOnPaste,
  statusFilter,
} from 'helpers/common'
import { CompanyService } from 'features/Company'

import { findSelected } from '../domain'
import type { BranchAddressLoadingProps } from 'features/Branch'

const thaiCountryCodeOption = { key: '+66', label: '+66' }

const BranchInfoSection: React.FC<BranchAddressLoadingProps> = ({
  setAddressLoading,
}: BranchAddressLoadingProps) => {
  const { control, errors, setValue, watch, trigger, clearErrors, getValues } =
    useFormContext<BranchForForm>()

  const { code, isSameAsCompany, companyId } = watch([
    'code',
    'isSameAsCompany',
    'companyId',
  ])

  const [branchForm, setBranchForm] = useRecoilState(branchFormState)
  const [codeChecking, setCodeChecking] = useState<boolean>(false)
  const [, setCompanyChecking] = useState<boolean>(false)
  const [nameChecking, setNameChecking] = useState<boolean>(false)
  const [companies, setCompanies] = useState<Company[]>([])
  const [searchCompanyOption, setSearchCompanyOption] =
    useState<ILoaderOption | null>(null)

  const { t } = useTranslation(['branch', 'common', 'company'])

  const base = 'f-branch__form'

  const optionBuilder = (companies?: Company[]): OptionProps[] | undefined => {
    if (!companies) {
      return
    }

    return companies.map(({ id, name }) => ({
      key: id,
      label: name,
    }))
  }

  const fetchCompanies = async (
    searchQuery: string,
    loadedOptions: unknown[],
    options: { page: number },
  ) => {
    const payload = {
      ...(searchQuery && {
        'name[like]': `%${searchQuery.trim()}%`,
      }),
      _limit: 10,
      _page: options.page,
      isActive: true,
    }

    const { body: _data, totals } = await findAll(payload)

    const currentTotal = loadedOptions.length + _data.length

    setCompanies(_data)

    const optionsWithTooltip = {
      options: _data.map((option) => ({
        value: option?.name,
        label:
          option?.name.length > 40 ? (
            <>
              <ReactTooltip
                role='tooltip'
                className='cb-select-tooltip'
                type='info'
                place='right'
                effect='solid'
                backgroundColor='#3B4050'
                id={option?.id}
              />
              <div
                data-for={option?.id}
                data-tip={option?.name}
                data-event='mouseover focus'
                data-event-off='mouseout blur'
                aria-label='option'
                className='selectOptionWithTooltip'
              >
                {option?.name}
              </div>
            </>
          ) : (
            option.name
          ),
        key: option.id,
        ...option,
      })),
      hasMore: currentTotal < totals,
      additional: {
        page: payload._page + 1,
      },
    }
    return optionsWithTooltip
  }

  const handleSearch = (item: ILoaderOption) => {
    const companyId: string = item.key
    const companyName: string = item.label

    setSearchCompanyOption(item)
    setValue('companyId', companyId)

    const selectedCompany: Company | undefined = companies?.find(
      (_company) => _company.id === companyId,
    )

    if (!selectedCompany) {
      handleCompanyFieldEdit(companyId)
      return
    } else {
      setBranchForm({
        ...branchForm,
        selectedCompany,
        companyName,
      })
    }

    setCompanyChecking(false)
    clearErrors('companyId')
  }

  const handleCompanyFieldEdit = async (companyId: string) => {
    setAddressLoading(true)

    try {
      const company = await CompanyService.findOneById(companyId)

      setBranchForm({
        ...branchForm,
        selectedCompany: company,
        companyName: branchForm.companyName,
      })

      setAddressLoading(false)
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  const setOriginalValue = (
    <div style={{ display: 'none' }}>
      <Controller
        as={TextInput}
        type='hidden'
        name='currentState'
        control={control}
      />
    </div>
  )

  const renderFieldBranchCode = (
    <FormControl
      className={`${base}__item`}
      label={t('branch:id.label')}
      required
    >
      <Controller
        type='text'
        name='code'
        control={control}
        render={(field) => (
          <TextInput
            value={field.value}
            onChange={field.onChange}
            onBlur={async () => {
              setCodeChecking(true)
              setValue('currentState', 'code')
              await trigger('code')
              setValue('currentState', '')
              setCodeChecking(false)
            }}
            placeholder={t('branch:insertId.placeholder')}
            isError={Boolean(errors.code?.message)}
            message={errors.code?.message}
            withIcon={codeChecking && <Loader />}
            ref={field.ref}
            maxLength={8}
            disabled={Boolean(code)}
          />
        )}
      />
    </FormControl>
  )

  const renderFieldBranchName = (
    <FormControl
      className={`${base}__item`}
      label={t('branch:name.label')}
      required
    >
      <Controller
        type='text'
        name='name'
        control={control}
        render={(field) => (
          <TextInput
            value={field.value}
            onChange={(e) => {
              field.onChange(trimFirstSpace(e))
            }}
            onBlur={async () => {
              setNameChecking(true)
              setValue('name', getValues('name').trim())
              await trigger('name')
              setNameChecking(false)
            }}
            placeholder={t('branch:insertName.placeholder')}
            isError={Boolean(errors.name?.message)}
            message={errors.name?.message}
            maxLength={100}
            withIcon={nameChecking && <Loader />}
            ref={field.ref}
          />
        )}
      />
    </FormControl>
  )

  useEffect(() => {
    if (Boolean(isSameAsCompany && companyId)) {
      handleCompanyFieldEdit(companyId)
    }
  }, [isSameAsCompany])

  return (
    <div className={`${base}__section`}>
      <span className={`${base}__title`}>{t('branch:info.label')}</span>

      {setOriginalValue}

      <div className={`${base}__info`}>
        {code && renderFieldBranchCode}
        {!code && renderFieldBranchName}

        <FormControl
          className={`${base}__item`}
          label={t('branch:status.label')}
        >
          <Controller
            name='isActive'
            control={control}
            render={(fields) => (
              <Switch
                className={`${base}__switch`}
                label={statusFilter(fields.value)}
                checked={fields.value}
                onChange={(e) => fields.onChange(e.target.checked)}
              />
            )}
          />
        </FormControl>

        {code && renderFieldBranchName}

        <FormControl
          className={`${base}__item`}
          htmlFor='branch telephone'
          label={t('common:phone.label')}
        >
          <InputGroup
            variants='ternary'
            error={errors.countryCode?.message || errors.phoneNumber?.message}
          >
            <Controller
              type='text'
              name='countryCode'
              control={control}
              render={(field) => (
                <Dropdown
                  ref={field.ref}
                  className='dropdown--telephone'
                  group='right'
                  options={[thaiCountryCodeOption]}
                  selected={findSelected([thaiCountryCodeOption], field.value)}
                  onChange={(value) => setValue('countryCode', value.key)}
                  isError={
                    Boolean(errors.countryCode?.message) ||
                    Boolean(errors.phoneNumber?.message)
                  }
                />
              )}
            />
            <Controller
              name='phoneNumber'
              control={control}
              render={(field) => (
                <TextInput
                  className={`${base}__phone-field`}
                  ref={field.ref}
                  placeholder={t('common:phone.placeholder')}
                  type='text'
                  group='left'
                  value={field.value}
                  maxLength={getPhoneNumberMaxLength(field.value)}
                  isError={
                    Boolean(errors.countryCode?.message) ||
                    Boolean(errors.phoneNumber?.message)
                  }
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    field.onChange(e.target.value)
                  }
                  onFocus={() =>
                    setValue('phoneNumber', field.value.replace('-', ''))
                  }
                  onBlur={formatPhoneNumberOnBlur((value) => {
                    setValue('phoneNumber', value)
                    trigger('phoneNumber')
                  })}
                  onKeyPress={validateKeyPress(new RegExp(/[\p{N}]/u))}
                  onPaste={(e) => handleDisableOnPaste(e)}
                />
              )}
            />
          </InputGroup>
        </FormControl>

        <FormControl
          className={`${base}__item`}
          label={t('company:name.label')}
          required
        >
          <Controller
            type='text'
            name='companyId'
            control={control}
            render={(field) => {
              const companyName: string = searchCompanyOption
                ? searchCompanyOption.label
                : branchForm.companyName

              const selected = setSelectedOption(
                field.value,
                companyName || branchForm.selectedCompany.name,
              )

              const isError =
                Boolean(errors.companyId?.message) &&
                Boolean(optionBuilder(companies))

              return (
                <AutoSuggest
                  ref={field.ref}
                  placeholder={t('company:searchCompanyName.placeholder')}
                  loadOptions={fetchCompanies}
                  message={errors.companyId?.message}
                  isError={isError}
                  onChange={(item) => {
                    field.onChange(item.key)
                    handleSearch(item)
                  }}
                  onBlur={async () => {
                    setCompanyChecking(true)
                    await trigger('companyId')
                    setCompanyChecking(false)
                  }}
                  selected={selected}
                  fullWidth
                  isSearchable
                />
              )
            }}
          />
        </FormControl>
      </div>
    </div>
  )
}

export default BranchInfoSection
